// Lightweight client-side email typo detector.
// Maps obvious typo domains to their likely corrections so we can prompt the
// user with a friendly "Quis dizer @gmail.com?" suggestion BEFORE the registration
// hits the database. Mirrors (but is intentionally less strict than) the backend
// TYPO_DOMAINS list in /app/backend/routes/email_routes.py.

const TYPO_TO_CORRECT = {
  // gmail
  "gmial.com": "gmail.com",
  "gmali.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gemail.com": "gmail.com",
  "gmail.cpm": "gmail.com",
  "gmail.cpom": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.om": "gmail.com",
  "gmail.coom": "gmail.com",
  "gmaill.com": "gmail.com",
  // hotmail
  "hotmial.com": "hotmail.com",
  "hotmali.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.cpom": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotmial.cm": "hotmail.com",
  "homail.com": "hotmail.com",
  "hormail.com": "hotmail.com",
  // yahoo
  "yaho.com": "yahoo.com",
  "yahho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yhoo.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
  // outlook
  "outloo.com": "outlook.com",
  "outloook.com": "outlook.com",
  "outlok.com": "outlook.com",
  "outllook.com": "outlook.com",
  // live / icloud
  "live.cpom": "live.com",
  "live.con": "live.com",
  "icloud.cpm": "icloud.com",
  "icloud.con": "icloud.com",
  "icoud.com": "icloud.com",
  // sapo (PT-specific)
  "sapo.pr": "sapo.pt",
  "sapo.pt.pt": "sapo.pt",
};

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

/**
 * Inspect an email string and return a suggestion if a typo domain is detected.
 * @param {string} email
 * @returns {{ original: string, suggestion: string } | null}
 *   - { suggestion } when a correction is available
 *   - null when the email is empty / valid / unrecognised
 */
export function suggestEmailFix(email) {
  if (!email || typeof email !== "string") return null;
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes("@")) return null;
  const [local, domain] = trimmed.split("@");
  if (!local || !domain) return null;
  const correct = TYPO_TO_CORRECT[domain];
  if (correct && correct !== domain) {
    return { original: trimmed, suggestion: `${local}@${correct}` };
  }
  return null;
}

/**
 * Return true if the address is structurally valid (RFC-5322 lite).
 * Does not check typos — use suggestEmailFix() for that.
 */
export function isStructurallyValidEmail(email) {
  if (!email || typeof email !== "string") return false;
  return EMAIL_RE.test(email.trim());
}
